import { Form, Formik } from "formik";
import { startCase } from "lodash";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { KTSVG } from "src/_metronic/helpers";
import { AskForPlanningForm, SimpleModal } from "src/app/components";
import Resume, { TContentType } from "src/app/components/PlanningForm/resumes/ResumeKeywords";
import Pacman from "src/app/components/layout/Pacman";
import {
    usePlanningByMoneySiteId,
    usePlanningMonthsOptions,
    useThematicsByRDT1Options,
} from "src/app/hooks";
import useProjectManagersOptions from "src/app/hooks/useProjectManagersOptions/useProjectManagersOptions";
import { PlanningTransporter } from "src/app/transports/planning";
import { Site } from "src/app/types/Site";
import { IActionsModal } from "src/app/types/actionsModal";
import { Planning, UpsertNewPlanningDTO, UpsertNewPlanningSchema } from "src/app/types/planning";

const CurrentPlanningModalContainer = (props: IActionsModal<Site>) => {
    const { handleClose = () => {}, entity, updateGridOnSave = () => {} } = props;
    const { formatMessage } = useIntl();
    const { planning, refresh: refreshPlanning } = usePlanningByMoneySiteId(entity?.id);
    const [planningEditId, setPlanningEditId] = useState<number | undefined>();
    const [loading, setLoading] = useState<boolean>(false);
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [currentPlanningAndOrderType, setCurrentPlanningAndOrderType] = useState<{
        planning: Planning;
        orderType: TContentType;
    } | null>(null);
    const { projectManagersOptions, loading: projectManagersOptionsLoading } =
        useProjectManagersOptions(true);
    const { planningMonthsOptions } = usePlanningMonthsOptions();
    const { thematicsOptions, refresh } = useThematicsByRDT1Options(entity?.id, false);

    useEffect(() => {
        if (entity && thematicsOptions.length === 0) {
            refresh();
        }
    }, [entity]);

    const showResume = (currentPlanning: Planning, orderType: TContentType) => {
        refresh();
        setCurrentPlanningAndOrderType({ planning: currentPlanning, orderType });
    };

    const closeResume = () => {
        setCurrentPlanningAndOrderType(null);
    };

    const handleToggle = async (el: Planning) => {
        setDisableButton(true);
        await PlanningTransporter.toggleActive(el.id);
        await refreshPlanning();
        setDisableButton(false);
    };

    const onSubmit = async (_values: UpsertNewPlanningDTO) => {
        setLoading(true);
        try {
            await PlanningTransporter.edit(_values);
            toast.success(formatMessage({ id: "common.notification.success" }));
            setPlanningEditId(undefined);
            updateGridOnSave(_values, true);
            refreshPlanning();
        } catch (error: any) {
            toast.error(
                error?.response?.data?.errors[0]?.message ||
                    formatMessage({ id: "common.notification.error" })
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SimpleModal
                entityName={formatMessage({ id: "common.entity.planning" })}
                handleClose={() => {
                    handleClose();
                    updateGridOnSave({}, true);
                }}
                loading={loading}
                formId=""
                buttonConfirmationAction={() => {
                    handleClose();
                    updateGridOnSave({}, true);
                }}
                buttonConfirmationLabel={formatMessage({ id: "common.action.confirmation" })}
                showModal={true}
            >
                <>
                    {planning.map((el, index) => (
                        <Fragment key={index}>
                            <span className="form-label">
                                {moment(el.createdAt).format("YYYY-MM-DD")},{" "}
                                <a
                                    className={
                                        !!el.orderType.filling && !!el.kwFilling
                                            ? "link-primary-hover"
                                            : "link-primary-hover-disable"
                                    }
                                    onClick={() =>
                                        !!el.orderType.filling &&
                                        !!el.kwFilling &&
                                        showResume(el, "filling")
                                    }
                                >
                                    {el.moneySite.content?.filling}{" "}
                                    {formatMessage({ id: "common.field.filling" })},{" "}
                                </a>
                                <a
                                    className={
                                        !!el.orderType.discover && !!el.kwDiscover
                                            ? "link-primary-hover"
                                            : "link-primary-hover-disable"
                                    }
                                    onClick={() =>
                                        !!el.orderType.discover &&
                                        !!el.kwDiscover &&
                                        showResume(el, "discover")
                                    }
                                >
                                    {el.moneySite.content?.discover}{" "}
                                    {formatMessage({ id: "common.field.discover" })},{" "}
                                </a>
                                <a
                                    className={
                                        !!el.orderType.lead && !!el.kwLead
                                            ? "link-primary-hover"
                                            : "link-primary-hover-disable"
                                    }
                                    onClick={() =>
                                        !!el.orderType.lead && !!el.kwLead && showResume(el, "lead")
                                    }
                                >
                                    {el.moneySite.content?.lead}{" "}
                                    {formatMessage({ id: "common.field.lead" })},{" "}
                                </a>
                                <a
                                    className={
                                        !!el.orderType.boost && !!el.kwBoost
                                            ? "link-primary-hover"
                                            : "link-primary-hover-disable"
                                    }
                                    onClick={() =>
                                        !!el.orderType.boost &&
                                        !!el.kwBoost &&
                                        showResume(el, "boost")
                                    }
                                >
                                    {el.moneySite.content?.boost}{" "}
                                    {formatMessage({ id: "common.field.boost" })},{" "}
                                </a>
                                <a
                                    className={
                                        !!el.orderType.ranking && !!el.kwRanking
                                            ? "link-primary-hover"
                                            : "link-primary-hover-disable"
                                    }
                                    onClick={() =>
                                        !!el.orderType.ranking &&
                                        !!el.kwRanking &&
                                        showResume(el, "ranking")
                                    }
                                >
                                    {el.moneySite.content?.ranking}{" "}
                                    {formatMessage({ id: "common.field.ranking" })},{" "}
                                </a>
                                <a
                                    className={
                                        !!el.orderType.netlinking && !!el.kwNetlinking
                                            ? "link-primary-hover"
                                            : "link-primary-hover-disable"
                                    }
                                    onClick={() =>
                                        !!el.orderType.netlinking &&
                                        !!el.kwNetlinking &&
                                        showResume(el, "netlinking")
                                    }
                                >
                                    {el.moneySite.content?.netlinking}{" "}
                                    {formatMessage({ id: "common.field.netlinking" })},{" "}
                                </a>
                                {el.months} {formatMessage({ id: "common.field.months" })},
                                {el.setAt ? (
                                    <>
                                        {" "}
                                        {formatMessage({ id: "common.field.kwSetOn" })}:{" "}
                                        <span className="text-primary">
                                            {moment(el.setAt).format("YYYY-MM-DD")}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        {" "}
                                        {!disableButton ? (
                                            <>
                                                <a
                                                    onClick={() => setPlanningEditId(el.id)}
                                                    title={startCase(
                                                        formatMessage({
                                                            id: "common.action.edit",
                                                        })
                                                    )}
                                                    className="inline-action"
                                                >
                                                    <KTSVG
                                                        path="/media/icons/duotune/art/art005.svg"
                                                        className="svg-icon-2"
                                                    />
                                                </a>{" "}
                                            </>
                                        ) : (
                                            <Pacman />
                                        )}
                                        {!disableButton ? (
                                            <a
                                                onClick={() => {
                                                    handleToggle(el);
                                                }}
                                                title={startCase(
                                                    formatMessage({
                                                        id: `common.action.${
                                                            el.active ? "disable" : "eneble"
                                                        }`,
                                                    })
                                                )}
                                                className="inline-action"
                                            >
                                                <KTSVG
                                                    path={`/media/icons/duotune/arrows/${
                                                        el.active ? "arr015.svg" : "arr013.svg"
                                                    }`}
                                                    className="svg-icon-2"
                                                />
                                            </a>
                                        ) : (
                                            <Pacman />
                                        )}
                                    </>
                                )}
                            </span>
                            <br />
                        </Fragment>
                    ))}
                </>
            </SimpleModal>
            <SimpleModal
                entityName={formatMessage({
                    id: `common.entity.${currentPlanningAndOrderType?.orderType || ""}Summary`,
                })}
                handleClose={closeResume}
                buttonConfirmationAction={closeResume}
                loading={loading}
                formId=""
                buttonConfirmationLabel={formatMessage({ id: "common.action.confirmation" })}
                showModal={!!currentPlanningAndOrderType}
            >
                <>
                    <Resume
                        months={currentPlanningAndOrderType?.planning.months}
                        moneySite={currentPlanningAndOrderType?.planning.moneySite}
                        kwFilling={currentPlanningAndOrderType?.planning.kwFilling}
                        kwDiscover={currentPlanningAndOrderType?.planning.kwDiscover}
                        kwLead={currentPlanningAndOrderType?.planning.kwLead}
                        kwBoost={currentPlanningAndOrderType?.planning.kwBoost}
                        kwRanking={currentPlanningAndOrderType?.planning.kwRanking}
                        kwNetlinking={currentPlanningAndOrderType?.planning.kwNetlinking}
                        contentType={currentPlanningAndOrderType?.orderType}
                        thematicsOptions={thematicsOptions}
                    />
                </>
            </SimpleModal>
            <SimpleModal
                entityName={formatMessage({
                    id: `common.entity.${currentPlanningAndOrderType?.orderType || ""}Summary`,
                })}
                handleClose={() => setPlanningEditId(undefined)}
                formId="editPlanning"
                showModal={!!planningEditId}
            >
                <Formik
                    initialValues={
                        PlanningTransporter.formTransformer(
                            planning.find((el) => el.id === planningEditId)
                        ) || {}
                    }
                    validationSchema={UpsertNewPlanningSchema}
                    onSubmit={(value: object) => onSubmit(value as UpsertNewPlanningDTO)}
                >
                    <Form id="editPlanning">
                        <AskForPlanningForm
                            planningMonthsOptions={planningMonthsOptions}
                            projectManagers={{
                                options: projectManagersOptions,
                                loading: projectManagersOptionsLoading,
                            }}
                        />
                    </Form>
                </Formik>
            </SimpleModal>
        </>
    );
};

export default CurrentPlanningModalContainer;
